import { StringType, BooleanType } from 'schema-typed';
import { convertSnakeCaseToCamelCase, objectHasProperty } from './utils';
import { websitePattern } from './regex';
import { cloneDeep } from 'lodash';
import { validationCompanyNameRegex, validationNameRegex } from './regex';
import { ERROR_EMAIL, ERROR_PHONE_FORMAT } from 'src/components/form/constants';

export const stringValidation = StringType();
export const nameRequiredValidation = StringType()
  .pattern(
    validationNameRegex,
    'May contain only letters, spaces and/or hyphens.'
  )
  .isRequired('Enter a name to continue');
export const companyNameValidation = StringType().pattern(
  validationCompanyNameRegex,
  'Company names must begin with ., @, & or an alphanumeric character.  The remainder of the name may only include -, !, ., @, &, (, ), alphanumeric characters or a single space character.'
);
export const companyNameRequiredValidation = StringType()
  .pattern(
    validationCompanyNameRegex,
    'Company names must begin with ., @, & or an alphanumeric character.  The remainder of the name may only include -, !, ., @, &, (, ), alphanumeric characters or a single space character.'
  )
  .isRequired('Enter a company name');
export const emailValidation = StringType().isEmail(ERROR_EMAIL);
export const uniqueEmailValidation = (
  existingEmails: string[], // eslint-disable-line @typescript-eslint/no-unused-vars
  errorMessage?: string // eslint-disable-line @typescript-eslint/no-unused-vars
) => StringType().isEmail(ERROR_EMAIL).isRequired('Enter an email address');
// Commenting the following as requested in https://deckardtech.atlassian.net/browse/CUS-13194
/*.addRule(
      (value) =>
        !existingEmails.some((e) => e.toLowerCase() === value?.toLowerCase()),
      errorMessage ?? 'Email address is already in use'
    )*/
export const emailRequiredValidation = StringType()
  .isEmail(ERROR_EMAIL)
  .isRequired('Enter an email address');
export const phoneValidation = StringType().pattern(
  /^\d{10}$/,
  ERROR_PHONE_FORMAT
);
export const phoneRequiredValidation = StringType()
  .pattern(/^\d{10}$/, ERROR_PHONE_FORMAT)
  .isRequired('Enter your 10-digit phone number');
export const phoneMask = [
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];
export const requiredValidation = StringType().isRequired(
  'Complete this field to continue'
);
export const requiredBooleanValidation = BooleanType().isRequired(
  'Select an option to continue'
);
// Should match with or without http or https
// - https://test.com
// - https://www.test.com
// - https://www.test.com/
// - https://www.test.com/aaa
// - https://www.test.com/aaa/bbb.html
// - www.test.com/aaa/bbb.html
// - test.com/aaa/bbb.html
// - etc
export const urlValidation = StringType().addRule((value) => {
  return websitePattern.test(value);
}, 'Enter a valid website URL (e.g., https://example.com)');
export const urlRequiredValidation = StringType()
  .addRule((value) => {
    return websitePattern.test(value);
  }, 'Enter a valid website URL (e.g., https://example.com)')
  .isRequired('Enter a valid website URL (e.g., https://example.com)');
export const checkedValidation = BooleanType().addRule((value) => {
  return value === true;
}, 'Check this box to continue');

export const getFlowFormValidationRuleWithRequirement = <T extends object>(
  propertyName: string,
  configOptions: T,
  validationRule: ReturnType<typeof StringType>,
  requiredMessage = 'Complete this field to continue'
) => {
  const parsedOptions: Record<string, unknown> = {};
  Object.keys(configOptions).forEach((key) => {
    const parsedKey = convertSnakeCaseToCamelCase(key);
    parsedOptions[parsedKey] = (configOptions as Record<string, unknown>)[key];
  });

  let isOptional = false;
  if (objectHasProperty(propertyName, parsedOptions)) {
    isOptional = parsedOptions[propertyName] === 'optional';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let coreRule = cloneDeep(validationRule) as any;
  if (isOptional) {
    coreRule.emptyAllowed = true;
    coreRule.required = false;
  }
  if (!isOptional) {
    coreRule = coreRule.isRequired(requiredMessage);
  }
  return coreRule;
};
